// The page: what has been accounted for, what has not, and the way on.
//
// Presentational and controlled. Everything it draws is derived from the
// entries it is given — coverage, gaps, ordering — so the bar can never
// disagree with the rows beneath it.

import {
  formatMonthIndex,
  formatMonthPeriod,
} from 'src/utils/date/formatMonthPeriod'
import {
  analyzeMonthCoverage,
  trailingMonthWindow,
} from 'src/utils/date/monthPeriods'
import type { MonthPeriod } from 'src/utils/date/monthPeriods'

import StepFooter from '../../components/StepFooter'
import { ChoiceRow } from './ChoiceRow'
import type { EntryAttention } from './EntryCard'
import { EntryCard } from './EntryCard'
import { GapRow } from './GapRow'
import { ProgressHeader } from './ProgressHeader'

export interface ListEntry {
  id: string
  kind: 'employment' | 'unemployment'
  name?: string
  /** Null when the entry has content but no dates yet — still shown, not hidden. */
  startMonth: number | null
  endMonth: number | null
  attention?: EntryAttention
  note?: string
}

export interface EmploymentHistoryListProps {
  entries: ListEntry[]
  /** Years the job listing asks for; null when it asks for no minimum. */
  requiredYears: number | null
  now: Date
  onOpenEntry: (id: string) => void
  onOpenPeriod: (period: MonthPeriod) => void
  onCurrentlyEmployed: () => void
  onNotCurrentlyEmployed: () => void
  onContinue: () => void
  onBack?: () => void
  backText?: string
  /** Set while a save is in flight, so Continue cannot race it. */
  busy?: boolean
}

const DEFAULT_YEARS = 10

export const EmploymentHistoryList = ({
  entries,
  requiredYears,
  now,
  onOpenEntry,
  onOpenPeriod,
  onCurrentlyEmployed,
  onNotCurrentlyEmployed,
  onContinue,
  onBack,
  backText,
  busy = false,
}: EmploymentHistoryListProps) => {
  const enforced = Boolean(requiredYears && requiredYears > 0)
  const window = trailingMonthWindow(
    enforced ? (requiredYears as number) : DEFAULT_YEARS,
    now
  )
  const dated = entries.filter(
    (entry): entry is ListEntry & { startMonth: number } =>
      entry.startMonth != null
  )
  const coverage = analyzeMonthCoverage(
    dated.map((entry) => ({ start: entry.startMonth, end: entry.endMonth })),
    window
  )

  // A gap only earns a row when there is history on both sides of it — a real
  // hole in the record. The stretch before the oldest job is not a hole, it is
  // simply how far back the driver has got, and the bar already says that.
  const earliestCovered = dated.length
    ? Math.min(...dated.map((entry) => entry.startMonth))
    : window.end + 1
  const internalGaps = coverage.gaps.filter(
    (gap) => gap.start > earliestCovered
  )
  const leadingGap = coverage.gaps.find((gap) => gap.end < earliestCovered)
  const coverageHint =
    internalGaps.length === 0 && leadingGap
      ? `Account for ${
          leadingGap.start === leadingGap.end
            ? formatMonthIndex(leadingGap.start)
            : formatMonthPeriod(leadingGap)
        } to continue`
      : 'Account for the remaining time to continue'

  const needingAttention = entries.filter(
    (entry) => entry.attention && entry.attention !== 'none'
  ).length
  // Coverage only blocks where the company asked for it. By default the bar
  // says what is left and that is the whole intervention.
  const blocked = needingAttention > 0 || (enforced && coverage.gaps.length > 0)

  const nowMonth = window.end
  // Undated entries first: they are the ones standing between the driver and a
  // save, so they belong where they will be seen.
  const ordered = [...entries].sort((a, b) => {
    if (a.startMonth == null) return -1
    if (b.startMonth == null) return 1
    return (b.endMonth ?? nowMonth) - (a.endMonth ?? nowMonth)
  })

  return (
    <div className="flex h-full flex-col">
      <ProgressHeader
        coveredMonths={coverage.coveredMonths}
        windowMonths={coverage.windowMonths}
        windowLabel={enforced ? undefined : 'of recent work history'}
      />

      <div className="flex flex-1 flex-col gap-2 overflow-y-auto py-2.5">
        {entries.length === 0 ? (
          <>
            <ChoiceRow
              label="I am currently employed"
              emphasis
              onSelect={onCurrentlyEmployed}
            />
            <ChoiceRow
              label="I am not currently employed"
              onSelect={onNotCurrentlyEmployed}
            />
          </>
        ) : (
          <>
            {ordered.map((entry) => (
              <EntryCard
                key={entry.id}
                name={entry.name}
                kind={entry.kind}
                startMonth={entry.startMonth}
                endMonth={entry.endMonth}
                attention={entry.attention}
                note={entry.note}
                nowMonth={nowMonth}
                onOpen={() => onOpenEntry(entry.id)}
              />
            ))}
            {internalGaps.map((gap) => (
              <GapRow
                key={gap.start}
                startMonth={gap.start}
                endMonth={gap.end}
                onResolve={() => onOpenPeriod(gap)}
              />
            ))}
            {/*
              Adding is an action, not something to infer from a coloured row.
              Two buttons, stacked, always in the same place.
            */}
            <div className="mt-1 flex flex-col gap-2">
              <button
                type="button"
                onClick={onCurrentlyEmployed}
                className="w-full rounded-lg border border-doubleNickel-brand-500 bg-doubleNickel-brand-25 p-3 text-center text-sm font-semibold text-doubleNickel-brand-700 hover:bg-doubleNickel-brand-50"
              >
                Add employment
              </button>
              <button
                type="button"
                onClick={onNotCurrentlyEmployed}
                className="w-full rounded-lg border border-doubleNickel-gray-300 bg-white p-3 text-center text-sm font-semibold text-doubleNickel-gray-700 hover:bg-doubleNickel-gray-50"
              >
                Add time not working
              </button>
            </div>
          </>
        )}
      </div>

      {entries.length === 0 ? (
        onBack && (
          <div className="hidden flex-none pb-[calc(0.25rem+env(safe-area-inset-bottom))] pt-3 sm:block">
            <StepFooter onBack={onBack} backText={backText} />
          </div>
        )
      ) : (
        <div className="flex-none border-t border-doubleNickel-gray-200 pb-[calc(0.25rem+env(safe-area-inset-bottom))] pt-3">
          {blocked && (
            <div className="mb-1.5 text-[11px] font-semibold text-doubleNickel-error-700">
              {needingAttention > 0
                ? `${needingAttention} ${
                    needingAttention === 1 ? 'entry needs' : 'entries need'
                  } your answer`
                : coverageHint}
            </div>
          )}
          <StepFooter
            primaryText="Save and continue"
            // Never greyed out: it explains instead, which is what a driver
            // needs in order to fix it
            onPrimary={() => {
              if (!blocked) onContinue()
            }}
            primaryDisabled={busy}
            onBack={onBack}
            backText={backText}
          />
        </div>
      )}
    </div>
  )
}
